import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('@FantasyADM:token');

  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

interface ApiErrorResponseData {
  message?: string;
  code?: string;
}

interface RefreshTokenResponseData {
  token: string;
  refresh_token: string;
}

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

interface QueuedRequest {
  config: RetryableRequestConfig;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ config, resolve, reject }) => {
    if (error) {
      reject(error);
      return;
    }

    config._retry = true;

    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    resolve(api(config));
  });
}

export function clearSession() {
  localStorage.removeItem('@FantasyADM:token');
  localStorage.removeItem('@FantasyADM:refreshToken');
  localStorage.removeItem('@FantasyADM:user');
  delete api.defaults.headers.common['Authorization'];
}

function redirectToLogin() {
  window.location.href = '/';
}

function isTokenErrorResponse(error: AxiosError<ApiErrorResponseData>): boolean {
  if (error.response?.status !== 401) {
    return false;
  }

  const data = error.response.data as ApiErrorResponseData | undefined;
  const message = data?.message;
  const code = data?.code;

  return (
    message === 'Token ausente' || message === 'token.invalid' || code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALID'
  );
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponseData>) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    const isRefreshRequest = originalRequest.url?.includes('/sessions/refreshtoken') ?? false;

    // Guard anti-loop: 401 vindo do próprio endpoint de refresh, requisição já
    // reprocessada uma vez, ou 401 que não é de token (ex.: permissão) — vai
    // direto para o fluxo de falha, sem tentar refresh.
    if (isRefreshRequest || originalRequest._retry || !isTokenErrorResponse(error)) {
      clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ config: originalRequest, resolve, reject });
      });
    }

    isRefreshing = true;
    originalRequest._retry = true;

    const refreshToken = localStorage.getItem('@FantasyADM:refreshToken');
    const body = refreshToken ? { refresh_token: refreshToken } : {};

    return api
      .post<RefreshTokenResponseData>('/sessions/refreshtoken', body)
      .then(({ data }) => {
        localStorage.setItem('@FantasyADM:token', data.token);
        localStorage.setItem('@FantasyADM:refreshToken', data.refresh_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;

        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${data.token}`;

        // Libera a fila e encerra o ciclo de refresh ANTES de reprocessar a
        // requisição original: `api(originalRequest)` pode levar um bom tempo
        // para assentar, e não pode segurar o reset de `isRefreshing`/
        // `failedQueue` — senão um 401 que chegue nesse meio-tempo entra na
        // fila deste ciclo (já finalizado) e nunca é resolvido/rejeitado.
        processQueue(null, data.token);
        isRefreshing = false;
        failedQueue = [];

        return api(originalRequest);
      })
      .catch((refreshError) => {
        processQueue(refreshError, null);
        isRefreshing = false;
        failedQueue = [];

        clearSession();
        redirectToLogin();

        return Promise.reject(refreshError);
      });
  },
);

export default api;
